import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { GameBoard } from '../components/game/GameBoard';
import { AbilityPanel } from '../components/game/AbilityPanel';
import { EndTurnButton } from '../components/game/EndTurnButton';
import { GameState, Ability, AbilityExecution } from '../types';
import api from '../services/api';
import websocketService from '../services/websocket';
import { MessageHandler } from '../services/messageHandler';

function GamePage() {
  const { roomId } = useParams<{ roomId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const user = authContext?.user;
  const isRejoin = searchParams.get('rejoin') === 'true';
  
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [selectedAbility, setSelectedAbility] = useState<Ability | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!roomId || !user) {
      navigate('/rooms');
      return;
    }

    const initializeGame = async () => {
      // If this is a rejoin, call the join endpoint to update connection status
      if (isRejoin) {
        try {
          console.log('Rejoining game...');
          await api.post(`/rooms/${roomId}/join`);
          console.log('Successfully rejoined game');
        } catch (err: any) {
          // If already connected, that's fine - continue loading game
          if (err.response?.status !== 400 || !err.response?.data?.error?.includes('already in this room')) {
            console.error('Failed to rejoin game:', err);
          }
        }
      }

      await fetchGameState();
    };

    initializeGame();
    
    // Set up WebSocket connection for real-time game updates
    const messageHandler = new MessageHandler({
      onTurnChanged: ({ room }) => {
        const gameState: GameState = {
          roomId: room.id,
          players: room.players,
          currentTurnPlayerId: room.currentTurnPlayerId || '',
          turnOrder: room.turnOrder || [],
          gameStatus: room.state,
        };
        setGameState(gameState);
      },
      onStatsUpdated: ({ room }) => {
        const gameState: GameState = {
          roomId: room.id,
          players: room.players,
          currentTurnPlayerId: room.currentTurnPlayerId || '',
          turnOrder: room.turnOrder || [],
          gameStatus: room.state,
        };
        setGameState(gameState);
      },
      onPlayerEliminated: ({ room }) => {
        const gameState: GameState = {
          roomId: room.id,
          players: room.players,
          currentTurnPlayerId: room.currentTurnPlayerId || '',
          turnOrder: room.turnOrder || [],
          gameStatus: room.state,
        };
        setGameState(gameState);
      }
    });

    websocketService.addMessageHandler(messageHandler.handleMessage);
    
    // Only connect if not already connected (might be coming from lobby)
    if (!websocketService.isConnected()) {
      websocketService.connect(roomId, user.id);
    }

    return () => {
      websocketService.removeMessageHandler(messageHandler.handleMessage);
      // Keep WebSocket connected for potential reconnection or other pages
      // websocketService.disconnect();
    };
  }, [roomId, user, navigate]);

  const fetchGameState = async () => {
    try {
      const response = await api.get(`/rooms/${roomId}`);
      const room = response.data;
      
      // Convert room data to game state format
      const gameState: GameState = {
        roomId: room.id,
        players: room.players,
        currentTurnPlayerId: room.currentTurnPlayerId || '',
        turnOrder: room.turnOrder || [],
        gameStatus: room.state,
      };
      
      setGameState(gameState);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch game state:', err);
      setError('Failed to load game state');
    } finally {
      setLoading(false);
    }
  };

  const handleAbilitySelect = (ability: Ability | null) => {
    setSelectedAbility(ability);
  };

  const handleExecuteAbility = async (abilityId: string, targetPlayerIds: string[]): Promise<AbilityExecution> => {
    try {
      const response = await api.post(`/rooms/${roomId}/execute-ability`, {
        abilityId,
        targetPlayerIds,
      });
      
      // Refresh game state after ability execution
      await fetchGameState();
      
      return response.data;
    } catch (err) {
      console.error('Failed to execute ability:', err);
      throw new Error('Failed to execute ability');
    }
  };

  const handleEndTurn = async () => {
    try {
      await api.post(`/rooms/${roomId}/end-turn`);
      
      // Refresh game state after ending turn
      await fetchGameState();
    } catch (err) {
      console.error('Failed to end turn:', err);
      throw new Error('Failed to end turn');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading game...</p>
        </div>
      </div>
    );
  }

  if (error || !gameState || !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Game not found'}</p>
          <button
            onClick={() => navigate('/rooms')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Back to Rooms
          </button>
        </div>
      </div>
    );
  }

  const currentPlayer = gameState.players.find(p => p.userId === user.id);
  const isCurrentPlayerTurn = gameState.currentTurnPlayerId === user.id;

  if (!currentPlayer) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">You are not a player in this game</p>
          <button
            onClick={() => navigate('/rooms')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Back to Rooms
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">
            LW Game - Room {roomId}
          </h1>
          <button
            onClick={() => navigate('/rooms')}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
          >
            Leave Game
          </button>
        </div>

        {/* Game Status Check */}
        {gameState.gameStatus === 'waiting' && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-800">
              Game is still in waiting state. All players must be ready to start.
            </p>
          </div>
        )}

        {gameState.gameStatus === 'completed' && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-800 font-semibold">
              Game has ended! Check your statistics to see the results.
            </p>
          </div>
        )}

        {/* Main Game Layout */}
        {gameState.gameStatus === 'in-progress' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Game Board - Takes up 2 columns */}
            <div className="lg:col-span-2">
              <GameBoard gameState={gameState} currentUserId={user.id} />
            </div>

            {/* Action Panel - Takes up 1 column */}
            <div className="space-y-6">
              {/* Ability Panel */}
              <AbilityPanel
                currentPlayer={currentPlayer}
                isCurrentPlayerTurn={isCurrentPlayerTurn}
                onAbilitySelect={handleAbilitySelect}
                onExecuteAbility={handleExecuteAbility}
                selectedAbility={selectedAbility}
                availablePlayers={gameState.players}
              />

              {/* End Turn Button */}
              <EndTurnButton
                isCurrentPlayerTurn={isCurrentPlayerTurn}
                onEndTurn={handleEndTurn}
                disabled={gameState.gameStatus !== 'in-progress'}
              />
            </div>
          </div>
        )}

        {/* Waiting/Completed State */}
        {gameState.gameStatus !== 'in-progress' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <GameBoard gameState={gameState} currentUserId={user.id} />
            
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Game Info</h3>
              <div className="space-y-3">
                <div>
                  <span className="font-medium text-gray-700">Status:</span>
                  <span className="ml-2 capitalize">{gameState.gameStatus}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Players:</span>
                  <span className="ml-2">{gameState.players.length}</span>
                </div>
                {gameState.turnOrder.length > 0 && (
                  <div>
                    <span className="font-medium text-gray-700">Turn Order Set:</span>
                    <span className="ml-2 text-green-600">Yes</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default GamePage;
